import { useEffect } from 'react'

import { useParams } from 'react-router-dom'

import { PaymentMethodEnum } from 'src/Constants/enums'
import { useDefaultPaymentMethod } from 'src/Hooks/useDefaultPaymentMethod'
import { getUserCountry } from 'src/Redux/auth-process/countrySlice/api-actions'
import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import {
  getCountry,
  isCountryLoaded,
} from 'src/Redux/auth-process/countrySlice/selectors'
import { getUserId } from 'src/Redux/auth-process/userSlice/selectors'
import { useGetBillingAgreementQuery } from 'src/Redux/billingAgreementApi'
import { setPaymentMethod } from 'src/Redux/payment-process'
import { getPaymentMethod } from 'src/Redux/payment-process/selectors'
import { changeRememberPayPalForFutureFlag } from 'src/Redux/paypalTransactions-process'
import { getRememberPayPalForFutureFlag } from 'src/Redux/paypalTransactions-process/selectors'
import { useGetMetaAdsCampaignByIdQuery } from 'src/Redux/adsCampaignApi'
import { getAllCampaignsCount, useMetadataQuery } from 'src/Redux/metadataApi'
import { useGetSubscriptionQuery } from 'src/Redux/subscription-process/subscriptionApi'
import { paymentMethodChanged } from 'src/Helpers/TagManager'
import { CampaignPlatformTypeEnum, CampaignTypeEnum } from 'src/Types/campaigns'

type UseAdsSchedulePaymentMethod = {
  hasSavePaypal: boolean
  paymentMethod: PaymentMethodEnum
  paypalEmail?: string
  isEnoughBalance: boolean
  isPaypalHidden: boolean
  isSavePaypalHidden?: boolean
  countryCode?: string
  onChange?: (value: PaymentMethodEnum) => void
  onChangeHasSavePaypal?: (value: boolean) => void
}

type Options = {
  isOrderBased?: boolean
}

export const useAdsSchedulePaymentMethod = ({
  isOrderBased = false,
}: Options = {}): UseAdsSchedulePaymentMethod => {
  const dispatch = useAppDispatch()
  const { number } = useParams()
  const campaignId = Number(number)
  const countryLoaded = useAppSelector(isCountryLoaded)
  const countryCode = useAppSelector(getCountry)
  const userId = useAppSelector(getUserId)
  const paymentMethod = useAppSelector(getPaymentMethod)
  const isRememberPayPalForFuture = useAppSelector(
    getRememberPayPalForFutureFlag,
  )
  const { data: billingData } = useGetBillingAgreementQuery(void 0, {
    skip: !isOrderBased,
  })
  const { data: metaAdsCampaign } = useGetMetaAdsCampaignByIdQuery(campaignId, {
    skip: !isOrderBased || !campaignId,
  })
  const { data: campaigns } = useMetadataQuery(null, { skip: !isOrderBased })
  const { data: plan } = useGetSubscriptionQuery(void 0, {
    skip: !isOrderBased,
  })

  useDefaultPaymentMethod({
    isMetaUrl: !isOrderBased,
    isEnoughBalance: false,
    hasBillingData: isOrderBased ? Boolean(billingData?.paypalEmail) : false,
  })

  useEffect(() => {
    if (!countryLoaded) {
      dispatch(getUserCountry())
    }
  }, [dispatch, countryLoaded])

  if (!isOrderBased) {
    return {
      paymentMethod: PaymentMethodEnum.STRIPE,
      isEnoughBalance: false,
      hasSavePaypal: false,
      isPaypalHidden: true,
      countryCode,
    }
  }

  const handleChangePaymentMethod = (value: PaymentMethodEnum): void => {
    paymentMethodChanged({
      campaignId,
      campaignNumber: getAllCampaignsCount(campaigns) || 1,
      campaignPlatform: CampaignPlatformTypeEnum.META,
      campaignType: CampaignTypeEnum.BASIC,
      genreList: '',
      paymentMethod: value,
      prevPaymentMethod: paymentMethod,
      resultPrice: metaAdsCampaign?.totalBudget || 0,
      userId: Number(userId),
      subscriptionPlan: plan?.package?.name || '',
    })
    dispatch(setPaymentMethod(value))
  }

  const handleChangeHasSavePaypal = (value: boolean): void => {
    dispatch(changeRememberPayPalForFutureFlag(value))
  }

  return {
    paymentMethod,
    countryCode,
    isEnoughBalance: false,
    hasSavePaypal: isRememberPayPalForFuture,
    isPaypalHidden: false,
    isSavePaypalHidden: true,
    paypalEmail: billingData?.paypalEmail,
    onChange: handleChangePaymentMethod,
    onChangeHasSavePaypal: handleChangeHasSavePaypal,
  }
}
